/*---------------------------------------------------------------------------------------------
 *  Copyright (c) 2023 Vienna Hypertext Inc. All rights reserved.
 *--------------------------------------------------------------------------------------------*/

import { useEffect, useRef } from "react";

import styled from "styled-components";

import { ProgrammingLanguage } from "@vienna/common";

interface Props {
  language: ProgrammingLanguage;
  changeLanguage: (l: ProgrammingLanguage) => void;
  selectedIndex: number | null;
  setSelectedIndex: (value: number | null) => void;
  index: number;
}

export default function LanguageOption({
  language,
  changeLanguage,
  selectedIndex,
  setSelectedIndex,
  index,
}: Props) {
  // option ref
  const optionRef = useRef<HTMLDivElement>(null);

  // scroll to option if selected
  useEffect(() => {
    if (selectedIndex === index) {
      const optionElement = optionRef.current;
      if (optionElement) {
        optionElement.scrollIntoView({ behavior: "smooth", block: "nearest" });
      }
    }
  }, [selectedIndex, index]);
  return (
    <StyledLanguageOption
      ref={optionRef}
      onClick={() => changeLanguage(language)}
      key={language}
      $isSelected={index === selectedIndex}
      onMouseEnter={() => setSelectedIndex(index)}
      onMouseLeave={() => setSelectedIndex(null)}
    >
      {language}
    </StyledLanguageOption>
  );
}

const StyledLanguageOption = styled.div<{ $isSelected: boolean }>`
  font-family: "IBM Plex Mono";
  padding: 5px;
  color: #aeaeae;
  font-size: 12px;
  background-color: ${({ theme, $isSelected }) => $isSelected && theme.subtleContrast};
  cursor: pointer;
`;
